import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Grid
} from '@mui/material';

const COLORS = [
  { id: 11, name: 'RED', color: '#FF0000' },
  { id: 20, name: 'WHITE', color: '#C1F1F9' },
  { id: 25, name: 'DEEP BLUE', color: '#2160FF' },
  { id: 21, name: 'GREEN', color: '#20A301' }
];

const TOTAL_STEPS = 5;

interface SetupColorModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (startingOffset: number[], endingOffset: number[]) => void;
}

const SetupColorModal: React.FC<SetupColorModalProps> = ({
  open,
  onClose,
  onSubmit
}) => {
  const [step, setStep] = useState(1);
  const [currentColorLeft, setCurrentColorLeft] = useState<number | null>(null);
  const [currentColorRight, setCurrentColorRight] = useState<number | null>(
    null
  );
  const [startingOffset, setStartingOffset] = useState<number[]>([]);
  const [endingOffset, setEndingOffset] = useState<number[]>([]);
  const [currentColorId, setCurrentColorId] = useState(0);
  const [currentColorIdRight, setCurrentColorIdRight] = useState(0);

  useEffect(() => {
    setCurrentColorId(0);
    setCurrentColorIdRight(0);
  }, [step]);

  const handleCloseModal = () => {
    setStartingOffset([]);
    setEndingOffset([]);
    setStep(1);
    setCurrentColorLeft(null);
    setCurrentColorRight(null);
    onClose();
  };

  useEffect(() => {
    if (
      startingOffset.length === TOTAL_STEPS &&
      endingOffset.length === TOTAL_STEPS
    ) {
      onSubmit(startingOffset, endingOffset);
      setTimeout(() => {
        handleCloseModal();
      }, 300);
    }
  }, [startingOffset, endingOffset]);

  const handleNext = () => {
    if (step < TOTAL_STEPS) {
      setStartingOffset([...startingOffset, currentColorLeft ?? 0]);
      setEndingOffset([...endingOffset, currentColorRight ?? 0]);
      setStep(step + 1);
    } else if (step === TOTAL_STEPS) {
      setStartingOffset([...startingOffset, currentColorLeft ?? 0]);
      setEndingOffset([...endingOffset, currentColorRight ?? 0]);
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseModal} fullWidth>
      <DialogTitle>
        Setup Color - Step {step} of {TOTAL_STEPS}
      </DialogTitle>
      <DialogContent>
        <LinearProgress
          variant="determinate"
          value={(step / TOTAL_STEPS) * 100}
        />
        <Typography variant="h6" mt={2}>
          Select Left Color
        </Typography>
        <Grid container spacing={2} mt={1}>
          {COLORS.map((color, index) => (
            <Grid item key={color.id} xs={3}>
              <Button
                style={{
                  backgroundColor:
                    currentColorId === color.id ? color.color : 'gray',
                  width: '100%'
                }}
                variant={currentColorId === color.id ? 'contained' : 'outlined'}
                onClick={() => {
                  setCurrentColorLeft(index + 1);
                  setCurrentColorId(color.id);
                }}
              >
                {color.name}
              </Button>
            </Grid>
          ))}
        </Grid>
        <Typography variant="h6" mt={2}>
          Select Right Color
        </Typography>
        <Grid container spacing={2} mt={1}>
          {COLORS.map((color, index) => (
            <Grid item key={color.id} xs={3}>
              <Button
                style={{
                  backgroundColor:
                    currentColorIdRight === color.id ? color.color : 'gray',
                  width: '100%'
                }}
                variant={
                  currentColorIdRight === color.id ? 'contained' : 'outlined'
                }
                onClick={() => {
                  setCurrentColorRight(index + 1);
                  setCurrentColorIdRight(color.id);
                }}
              >
                {color.name}
              </Button>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleNext}
          color="primary"
          disabled={!currentColorLeft || !currentColorRight}
        >
          Next
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetupColorModal;
